/** Завантаження курсу і швидкі індекси по ньому. */
import type {
  Course, Insight, Module, Phrase, Roleplay, RoleplayNode, RoleplayOption,
  Dialogue, DialogueLine, Note,
} from '../../content/schema.ts'

export type {
  Course, Module, Phrase, Insight, Roleplay, RoleplayNode, RoleplayOption,
  Dialogue, DialogueLine, Note,
}

export interface CourseIndex {
  course: Course
  modules: Module[]
  moduleById: Map<string, Module>
  phraseById: Map<string, Phrase>
  /** Модуль, якому належить фраза. */
  moduleOfPhrase: Map<string, string>
  /** Порядок введення нового матеріалу — послідовність id фраз. */
  order: string[]
}

let cached: CourseIndex | null = null

export async function loadCourse(): Promise<CourseIndex> {
  if (cached) return cached
  const res = await fetch(`${import.meta.env.BASE_URL}content/course.json`)
  if (!res.ok) throw new Error(`Не вдалося завантажити курс: ${res.status}`)
  const course = (await res.json()) as Course
  cached = index(course)
  return cached
}

export function index(course: Course): CourseIndex {
  const modules = [...course.modules].sort((a, b) => a.order - b.order)
  const moduleById = new Map(modules.map((m) => [m.id, m]))
  const phraseById = new Map<string, Phrase>()
  const moduleOfPhrase = new Map<string, string>()
  const order: string[] = []

  for (const m of modules) {
    for (const p of m.phrases) {
      phraseById.set(p.id, p)
      moduleOfPhrase.set(p.id, m.id)
      order.push(p.id)
    }
  }
  return { course, modules, moduleById, phraseById, moduleOfPhrase, order }
}

/** Фрази модуля. */
export function phrasesOf(idx: CourseIndex, moduleId: string): Phrase[] {
  return idx.moduleById.get(moduleId)?.phrases ?? []
}

/** Скільки фраз модуля вже введено. */
export function moduleProgress(
  idx: CourseIndex,
  moduleId: string,
  isIntroduced: (phraseId: string) => boolean,
): { done: number; total: number } {
  const phrases = phrasesOf(idx, moduleId)
  return { done: phrases.filter((p) => isIntroduced(p.id)).length, total: phrases.length }
}

/** Кілька випадкових «відволікачів» для вправи з вибором відповіді. */
export function distractors(idx: CourseIndex, phrase: Phrase, count: number, seed: number): Phrase[] {
  const sameModule = phrasesOf(idx, idx.moduleOfPhrase.get(phrase.id) ?? '')
  // Спершу шукаємо схожі за типом — так завдання складніше й корисніше.
  const pool = sameModule.filter((p) => p.id !== phrase.id && p.kind === phrase.kind)
  const fallback = [...idx.phraseById.values()].filter((p) => p.id !== phrase.id)
  const source = pool.length >= count ? pool : fallback
  const out: Phrase[] = []
  let s = seed >>> 0 || 1
  const used = new Set<string>()
  let guard = 0
  while (out.length < count && guard++ < 500) {
    s ^= s << 13; s >>>= 0
    s ^= s >> 17
    s ^= s << 5; s >>>= 0
    const cand = source[s % source.length]
    if (cand && !used.has(cand.id) && cand.uk !== phrase.uk) {
      used.add(cand.id)
      out.push(cand)
    }
  }
  return out
}
